import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { Steps, Button, Icon, Spin, Tooltip, message, Modal, Alert } from 'antd'
import './styles.scss'
import NewHeader from '../../components/NewHeader'
import UploadDocument from './components/UploadDocument'
import Voiceover from './components/Voiceover'
import ShotList from './components/ShotList'
import ImageGeneration from './components/ImageGeneration'
import VideoGeneration from './components/VideoGeneration'
import VideoStyle from './components/VideoStyle'
import { useImageGeneration } from './components/ImageGeneration/hooks/useImageGeneration'
import { useVideoGeneration } from './components/VideoGeneration/hooks/useVideoGeneration'
import { useFinalVideoGeneration } from './components/VideoGeneration/hooks/useFinalVideoGeneration'
import {
  useCharacters,
  useVideoDataPersistence,
  useLocationReferences,
  useAudioClustering,
  useShotsGeneration,
  useStepNavigation,
  useVoiceManagement,
  useCalculateDurations,
  useDraftShotsGeneration,
  usePropReferences,
  useWardrobe,
  defaultData
} from './hooks'
import {
  constructAssemblyPayload,
  formatDurationOutput
} from './utils/videoUtils'

import { changeMachineState } from '../../services/api/aiVideoGeneration'
import history from '../../history'
import { ModeSelectionModal, DraftModePage } from './components/DraftMode'
import GenerationStatusModal from './components/VideoGeneration/components/GenerationStatusModal'
import AIVideoModal from './components/VideoGeneration/components/FinalVideo'
import FinalVideoDisplay from './components/FinalVideoDisplay/FinalVideoDisplay'

const { Step } = Steps

const VideoAi = props => {
  const videoGenerationRef = useRef(null)
  const imageGenerationRef = useRef(null)

  const [showProcessShots, setShowProcessShots] = useState(false)
  const [isFineTunedLoading, setIsFineTunedLoading] = useState(false)
  const [calculationStarting, setCalculationStarting] = useState(false)
  const [formData, setFormData] = useState({
    ...defaultData,
    durationsReady: false
  })
  const formDataRef = useRef(formData)
  const isCalculatingRef = useRef(false)
  const initialLoadRef = useRef(true)
  const fetchStartedRef = useRef(false)

  // Draft Mode State
  const [showModeModal, setShowModeModal] = useState(false)
  const [isDraftMode, setIsDraftMode] = useState(false)

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  const { videoId, initialStep, handleGoBack } = props

  // Image and Video providers
  const [imageProvider, setImageProvider] = useState('DeepAI')
  const [isImageGenerationLoading, setIsImageGenerationLoading] = useState(
    false
  )
  const [videoProvider, setVideoProvider] = useState('luma')

  // UI State for Modals
  const [showGenerationModal, setShowGenerationModal] = useState(false)
  const [showFinalVideoModal, setShowFinalVideoModal] = useState(false)

  // Define steps configuration
  const steps = useMemo(() => {
    const stepList = [
      {
        key: 'uploadDocument',
        title: 'Upload',
        icon: 'file-text',
        component: UploadDocument
      },
      {
        key: 'voiceover',
        title: 'Voiceover',
        icon: 'sound',
        component: Voiceover
      },
      {
        key: 'videoStyle',
        title: 'Visual Setup',
        icon: 'camera',
        component: VideoStyle
      },
      {
        key: 'shotList',
        title: 'Shot List',
        icon: 'ordered-list',
        component: ShotList
      },
      {
        key: 'imageGeneration',
        title: 'Images',
        icon: 'picture',
        component: ImageGeneration
      },
      {
        key: 'videoGeneration',
        title: 'Video',
        icon: 'video-camera',
        component: VideoGeneration
      }
    ]

    if (formData.finalVideo) {
      stepList.push({
        key: 'finalVideo',
        title: 'Final Result',
        icon: 'play-circle',
        component: FinalVideoDisplay
      })
    }

    return stepList
  }, [formData.finalVideo])

  // Helper to set scenes in formData
  const setScenes = action => {
    setFormData(prev => {
      const currentScenes = prev.shotList || []
      const newScenes =
        typeof action === 'function' ? action(currentScenes) : action
      return { ...prev, shotList: newScenes }
    })
  }

  // Step Navigation Hook
  const {
    currentStep,
    stepsValidation,
    handleNext,
    handlePrevious,
    navigateToStep,
    updateStepValidation,
    setStepsValidation,
    getPreviousNextStepTitles,
    shouldShowNextButton,
    isAllScenesGenerated
  } = useStepNavigation({
    videoId,
    initialStep,
    steps,
    isFetchingVideoData: false // Will be set properly below
  })

  // Location References Hook
  const {
    isGeneratingLocationReference,
    locationGenerationProgress,
    locationGenerationStatus,
    generateLocationMap,
    handleAddLocationReference,
    handleEditLocationReference,
    handleBatchUpdateLocations
  } = useLocationReferences({
    formData,
    setFormData,
    videoId // Pass videoId
  })

  // Prop References Hook
  const {
    isGeneratingPropReference,
    propGenerationProgress,
    propGenerationStatus,
    generatePropMap,
    handleAddPropReference,
    handleEditPropReference,
    handleBatchUpdateProps
  } = usePropReferences({
    formData,
    setFormData,
    videoId // Pass videoId
  })

  // Shots Generation Hook
  const shotsHook = useShotsGeneration({
    formData,
    setFormData,
    videoId, // Pass videoId
    onLocationMapGenerate: generateLocationMap,
    onAudioClustering: (sceneData, data) =>
      initiateAudioClustering(sceneData, data),
    parseJSONSafely: (val, maxDepth = 5) => {
      if (val === null || val === undefined) return null
      let result = val
      let depth = 0
      while (typeof result === 'string' && depth < maxDepth) {
        try {
          result = JSON.parse(result)
          depth++
        } catch {
          break
        }
      }
      return result
    }
  })

  const {
    isFetchingShots,
    shotsGenerationProgress,
    formatSceneObj,
    fetchShots
  } = shotsHook

  // Audio Clustering Hook
  const {
    isClusteringAudio,
    audioClusteringProgress,
    audioClusteringStatus,
    initiateAudioClustering,
    formatShotListForClustering,
    resumeAudioPolling
  } = useAudioClustering({
    formData,
    setFormData,
    formatSceneObj,
    videoId // Pass videoId
  })

  // Connections and side effects are handled below

  // Video Data Persistence Hook
  const {
    isFetchingVideoData,
    isSavingVideoData,
    isCloning,
    fetchVideoData,
    handleSaveVideoData,
    handleUpdateVideoData,
    cloneVideoWithStyle,
    parseJSONSafely,
    error
  } = useVideoDataPersistence({
    videoId,
    formData,
    setFormData,
    setStepsValidation,
    steps,
    currentStep,
    onLocationMapGenerate: generateLocationMap
  })

  // Voice Management Hook
  const { voices, setVoices, handleVoiceChange } = useVoiceManagement({
    formData,
    setFormData,
    videoId, // Pass videoId
    onAudioClustering: initiateAudioClustering,
    formatShotListForClustering
  })

  // Characters Hook
  const {
    characters,
    setCharacters,
    isFetchingCharacters,
    fetchCharacters
  } = useCharacters({
    videoId, // Pass videoId
    onCharactersLoaded: chars => {
      setFormData(prev => ({ ...prev, characterList: chars }))
    }
  })

  // Duration Calculation Hook
  const {
    calculate: callCalculateDurations,
    isCalculating: isCalculatingDurations,
    progress: calculationProgress
  } = useCalculateDurations({ videoId }) // Pass videoId

  // Wardrobe Hook
  const {
    fetchLibrary: fetchWardrobeLibrary,
    isLoading: isWardrobeLoading
  } = useWardrobe({ videoId }) // Pass videoId

  // Draft Shots Generation Hook
  const draftShotsHook = useDraftShotsGeneration({
    parseJSONSafely,
    videoId // Pass videoId
  })

  // Fingerprint of data that affects durations
  const durationFingerprint = useMemo(() => {
    if (!formData.shotList) return ''
    try {
      return JSON.stringify(
        formData.shotList.map(scene => ({
          id: scene.id,
          voiceover: scene.aggregated_voiceover,
          shots: (scene.shots || []).map(shot => ({
            id: shot.id,
            text: shot.voiceover_text
          }))
        }))
      )
    } catch (e) {
      return ''
    }
  }, [formData.shotList])

  // Reset durationsReady if data fingerprint changes
  useEffect(() => {
    if (formData.durationsReady) {
      setFormData(prev => ({ ...prev, durationsReady: false }))
    }
  }, [durationFingerprint])

  const handleStartDurationCalculation = useCallback(async () => {
    const currentData = formDataRef.current
    if (
      isCalculatingRef.current ||
      !currentData.shotList ||
      currentData.shotList.length === 0
    )
      return

    isCalculatingRef.current = true
    setCalculationStarting(true)
    try {
      const payload = constructAssemblyPayload(
        currentData.shotList,
        currentData.locationMap
      )
      // Exclude location_map from shooting_list as per requirement
      const {
        location_map,
        ...shootingListWithoutLocationMap
      } = payload.clustered_shooting_list

      const output = await callCalculateDurations(
        {
          shooting_list: shootingListWithoutLocationMap
        },
        formatDurationOutput,
        currentData.shotList
      )

      if (output) {
        setFormData(prev => ({
          ...prev,
          calculatedShotList: output,
          durationsReady: true
        }))
      }
    } catch (error) {
      console.error('Duration calculation failed:', error)
    } finally {
      isCalculatingRef.current = false
      setCalculationStarting(false)
    }
  }, [callCalculateDurations])

  // Trigger duration calculation when entering Video Generation step
  useEffect(() => {
    const isVideoStep = steps[currentStep]?.key === 'videoGeneration'
    if (
      isVideoStep &&
      !isCalculatingDurations &&
      !isCalculatingRef.current &&
      !formData.durationsReady &&
      formData.shotList?.length > 0
    ) {
      handleStartDurationCalculation()
    }
  }, [
    currentStep,
    isCalculatingDurations,
    formData.durationsReady,
    formData.shotList?.length,
    handleStartDurationCalculation,
    steps.length
  ])

  // Image Generation Hook
  const {
    generateAllImages,
    handleRegenerateImage,
    retryFailedImages,
    handleFileUpload,
    isUploading: isUploadingImage
  } = useImageGeneration({
    scenes: formData.shotList || [],
    setScenes,
    formData,
    imageProvider,
    setIsLoading: setIsImageGenerationLoading,
    videoId // Pass videoId
  })

  const {
    videoGenerating,
    generatingVideoForAllScenes,
    handleGenerateFullVideo: generateAllVideos,
    handleRegenerateVideo,
    handleRetryFailedVideos,
    updateShotDataRef
  } = useVideoGeneration({
    scenes: formData.shotList || [],
    setScenes,
    formData,
    videoProvider,
    videoId // Pass videoId
  })

  // Final Video Generation Hook (New)
  const {
    isGeneratingFinalVideo,
    finalVideoError,
    generateFinalVideo,
    setFinalVideoError
  } = useFinalVideoGeneration({
    formData,
    setFormData,
    videoId // Pass videoId
  })

  // Sync modal visibility with generation state
  useEffect(() => {
    if (isGeneratingFinalVideo) {
      setShowGenerationModal(true)
    }
  }, [isGeneratingFinalVideo])

  // Automatically open video player when generation completes
  const wasGeneratingRef = useRef(false)
  useEffect(() => {
    if (isGeneratingFinalVideo) {
      wasGeneratingRef.current = true
    } else if (
      wasGeneratingRef.current &&
      formData.finalVideo &&
      !finalVideoError
    ) {
      wasGeneratingRef.current = false
      // Instead of showing modal, navigate to the final step
      if (steps.some(s => s.key === 'finalVideo')) {
        navigateToStep(steps.length - 1)
      }
    }
  }, [
    isGeneratingFinalVideo,
    formData.finalVideo,
    finalVideoError,
    steps,
    navigateToStep
  ])

  // Fetch video data on mount/videoId change
  useEffect(() => {
    if (videoId) {
      fetchVideoData()
    }
  }, [videoId])

  // Fetch characters if needed
  useEffect(() => {
    const shouldFetchCharacters =
      !formData.characterList || formData.characterList.length === 0
    if (shouldFetchCharacters) {
      fetchCharacters()
    }
  }, [formData.characterList])

  // Reset initialLoadRef when data is successfully fetched or if it's a new video
  useEffect(() => {
    if (isFetchingVideoData) {
      fetchStartedRef.current = true
    }
  }, [isFetchingVideoData])

  useEffect(() => {
    if (!videoId) {
      initialLoadRef.current = false
    } else if (fetchStartedRef.current && !isFetchingVideoData) {
      initialLoadRef.current = false
    }
  }, [isFetchingVideoData, videoId])

  // Resume audio polling if there's a pending audio task (e.g., after browser refresh)
  useEffect(() => {
    if (
      !isFetchingVideoData &&
      formData.audio?.id &&
      formData.audio?.status === 'pending' &&
      !isClusteringAudio
    ) {
      resumeAudioPolling(formData.audio.id)
    }
  }, [
    isFetchingVideoData,
    formData.audio?.id,
    formData.audio?.status,
    isClusteringAudio,
    resumeAudioPolling
  ])

  // Auto-save on form data change
  useEffect(() => {
    if (!initialLoadRef.current) {
      handleSaveVideoData()
    }
  }, [formData])

  // Handle story text submission: Show mode selection instead of auto-fetch
  useEffect(() => {
    if (
      formData.voiceoverText &&
      formData.shotList.length === 0 &&
      !formData.shotLoading &&
      !isDraftMode
    ) {
      // Instead of auto-fetching, ask user for mode
      setShowModeModal(true)
    }
  }, [
    formData.shotList,
    formData.shotLoading,
    isDraftMode,
    formData.voiceoverText
  ])

  // Redirect to draft mode if there is a pending draft on initial load
  useEffect(() => {
    if (
      !isFetchingVideoData &&
      formData.draft &&
      formData.draft.id &&
      formData.draft.status === 'pending' &&
      !isDraftMode
    ) {
      setIsDraftMode(true)
    }
  }, [isFetchingVideoData, formData.draft, isDraftMode])

  // Redirect to video generation step if final video is pending (New)
  useEffect(() => {
    if (
      !isFetchingVideoData &&
      !initialLoadRef.current &&
      formData.video?.status === 'pending' &&
      steps[currentStep]?.key !== 'videoGeneration'
    ) {
      // Find index of videoGeneration
      const videoIndex = steps.findIndex(s => s.key === 'videoGeneration')
      if (videoIndex !== -1) navigateToStep(videoIndex)
    }
  }, [
    isFetchingVideoData,
    formData.video?.status,
    currentStep,
    steps.length,
    navigateToStep
  ])

  // Trigger location generation if user moves past uploadDocument step and references are missing
  useEffect(() => {
    const isUploadStep = steps[currentStep]?.key === 'uploadDocument'
    const hasLocationMap =
      formData.locationMap && Object.keys(formData.locationMap).length > 0
    const hasLocationReference =
      formData.locationReference &&
      Object.keys(formData.locationReference).length > 0
    const hasPendingDraft =
      formData.draft && formData.draft.id && formData.draft.status === 'pending'

    if (
      !isUploadStep &&
      !isDraftMode &&
      !hasPendingDraft &&
      hasLocationMap &&
      !hasLocationReference &&
      !isGeneratingLocationReference
    ) {
      generateLocationMap(formData.locationMap)
    }
  }, [
    currentStep,
    isDraftMode,
    isGeneratingLocationReference,
    formData.locationMap,
    formData.locationReference,
    formData.draft,
    generateLocationMap,
    steps
  ])

  // Trigger prop generation if user moves past uploadDocument step and references are missing
  useEffect(() => {
    const isUploadStep = steps[currentStep]?.key === 'uploadDocument'
    const hasPropMap =
      formData.propMap && Object.keys(formData.propMap).length > 0
    const hasPropReference =
      formData.props_reference &&
      Object.keys(formData.props_reference).length > 0
    const hasPendingDraft =
      formData.draft && formData.draft.id && formData.draft.status === 'pending'

    if (
      !isUploadStep &&
      !isDraftMode &&
      !hasPendingDraft &&
      hasPropMap &&
      !hasPropReference &&
      !isGeneratingPropReference
    ) {
      generatePropMap(formData.propMap)
    }
  }, [
    currentStep,
    isDraftMode,
    isGeneratingPropReference,
    formData.propMap,
    formData.props_reference,
    formData.draft,
    generatePropMap,
    steps
  ])

  // Trigger wardrobe library fetch if user reaches shotList step and outfits are missing
  useEffect(() => {
    const isShotListStep = steps[currentStep]?.key === 'shotList'
    const hasOutfits = formData.outfits && formData.outfits.length > 0
    const shotListIndex = steps.findIndex(s => s.key === 'shotList')
    const shouldFetch =
      isShotListStep || (shotListIndex !== -1 && currentStep > shotListIndex)

    if (
      shouldFetch &&
      !hasOutfits &&
      !isWardrobeLoading &&
      !isFetchingVideoData
    ) {
      fetchWardrobeLibrary().then(response => {
        if (response) {
          // Handle both array and object response formats
          const items = Array.isArray(response)
            ? response
            : response.items || []
          const pagination = !Array.isArray(response)
            ? response.pagination
            : null

          if (items.length > 0) {
            setFormData(prev => ({
              ...prev,
              outfits: items,
              outfitsPagination: pagination
            }))
          }
        }
      })
    }
  }, [
    currentStep,
    formData.outfits,
    isWardrobeLoading,
    isFetchingVideoData,
    fetchWardrobeLibrary,
    steps
  ])

  // Trigger audio clustering when user reaches shotList step and no audio exists
  useEffect(() => {
    const isShotListStep = steps[currentStep]?.key === 'shotList'
    const hasShots = formData.shotList && formData.shotList.length > 0
    const hasSelectedVoice = !!formData.selectedVoice
    const hasPendingDraft =
      formData.draft && formData.draft.id && formData.draft.status === 'pending'

    // Check if ANY scene has audio_link - if so, don't trigger clustering
    const hasAnyAudioLink =
      hasShots && formData.shotList.some(scene => !!scene.audio_link)

    if (
      isShotListStep &&
      hasShots &&
      hasSelectedVoice &&
      !hasAnyAudioLink &&
      !isClusteringAudio &&
      !isDraftMode &&
      !hasPendingDraft
    ) {
      // Trigger audio clustering for all scenes
      const formattedData = formatShotListForClustering(formData.shotList)
      initiateAudioClustering(formattedData, null, {
        voiceId: formData.selectedVoice
      })
    }
  }, [
    currentStep,
    formData.shotList,
    formData.selectedVoice,
    formData.draft,
    isClusteringAudio,
    isDraftMode,
    initiateAudioClustering,
    formatShotListForClustering,
    steps
  ])

  const handleSelectMode = mode => {
    setShowModeModal(false)
    if (mode === 'manual') {
      // Proceed with existing manual flow
      if (!formData.shotList?.length) {
        shotsHook.fetchShots()
      }
      // Manually navigate to the next step (Voiceover) since we removed auto-advance
      handleNext()
    } else if (mode === 'draft') {
      setIsDraftMode(true)
    }
  }

  const handleFinishDraft = async draftData => {
    try {
      const updatedFormData = {
        ...formData,
        shotList: draftData.shot_list || [],
        locationMap: parseJSONSafely(draftData.location_map),
        locationReference: parseJSONSafely(draftData.location_reference),
        propMap: parseJSONSafely(draftData.prop_map),
        props_reference: parseJSONSafely(
          draftData.props_reference || draftData.prop_reference
        ),
        characterMap: parseJSONSafely(draftData.character_map),
        selectedCharacter: parseJSONSafely(draftData.selected_character),
        selectedVoice: draftData.selected_voice,
        aspectRatio: draftData.aspect_ratio || '16:9',
        videoStyle: draftData.video_style,
        outfits_reference: parseJSONSafely(draftData.outfits_reference),
        transitions: parseJSONSafely(draftData.transitions),
        finalVideo: draftData.video_url || '',
        draft: {
          ...formData.draft,
          status: 'completed'
        }
      }
      await handleUpdateVideoData(updatedFormData)

      await fetchVideoData()

      setIsDraftMode(false)

      navigateToStep(3)
      message.success('Draft processed successfully! Review your scenes below.')
    } catch (error) {
      console.error('Failed to finalize draft:', error)
      message.error('Failed to process draft results.')
    }
  }

  // Handle video style change with Clone/Override
  const handleVideoStyleChange = async (
    newStyle,
    shouldClone,
    type = 'video_style'
  ) => {
    if (shouldClone) {
      try {
        const newVideoId = await cloneVideoWithStyle(newStyle, null, type)
        const stepKey = steps[currentStep]?.key || 'videoStyle'

        Modal.success({
          title: 'Video Cloned Successfully',
          content:
            'You are now editing a new copy of the video. The original video remains unchanged.',
          onOk: () => {
            history.push(`/video-create/${newVideoId}/${stepKey}`)
          },
          centered: true
        })
      } catch (error) {
        message.error('Failed to clone video')
      }
    } else {
      const hasGeneratedContent =
        formData.shotList &&
        formData.shotList.length > 0 &&
        formData.shotList.some(scene => scene.shots.some(shot => shot.imageSrc))

      if (type === 'shot_style') {
        setFormData(prev => ({
          ...prev,
          shotGenerationStyle: newStyle,
          editingStyle: true,
          shotList: [],
          calculatedShotList: null,
          locationMap: {},
          locationReference: {},
          characterMap: {}
        }))

        // Trigger new shot generation with the new style id
        setTimeout(() => {
          shotsHook.fetchShots(newStyle.id)
        }, 100)

        message.info('Existing shots removed. Regenerating with new style...')
      } else {
        setFormData(prev => ({
          ...prev,
          videoStyle: newStyle,
          ...(hasGeneratedContent && {
            shotList: prev.shotList.map(scene => ({
              ...scene,
              shots: scene.shots.map(shot => ({
                ...shot,
                imageSrc: null,
                vidSrc: null
              }))
            }))
          })
        }))

        if (hasGeneratedContent) {
          message.info('Images will be regenerated with new style')
        }
      }
    }
  }

  // Handle voice change with Clone/Override
  const handleVoiceChangeWithClone = async (voiceId, shouldClone) => {
    if (shouldClone) {
      try {
        // Clone video with new voice only (pass null for style to preserve images)
        const newVideoId = await cloneVideoWithStyle(null, voiceId)
        const stepKey = steps[currentStep]?.key || 'voiceover'

        Modal.success({
          title: 'Video Cloned Successfully',
          content:
            'You are now editing a new copy of the video with the new voice. The original video remains unchanged.',
          onOk: () => {
            history.push(`/video-create/${newVideoId}/${stepKey}`)
          },
          centered: true
        })
      } catch (error) {
        message.error('Failed to clone video')
      }
    } else {
      // Override: use the existing handleVoiceChange from the hook
      handleVoiceChange(voiceId)
    }
  }

  // Component ref handlers
  const handleOpenVideoEditor = () => {
    if (videoGenerationRef.current?.openVideoEditor) {
      videoGenerationRef.current.openVideoEditor()
    } else {
      console.error('Video generation function not available')
    }
  }

  // Machine state handlers
  const startMachine = async () => await changeMachineState(1)
  const stopMachine = async () => await changeMachineState(0)

  const showModalMessage = ({
    title = 'Info',
    content = '',
    type = 'info'
  }) => {
    Modal[type]({
      title,
      content,
      centered: true
    })
  }

  // Handle safe navigation
  const handleSafeNext = () => {
    const isImageGenerationStep = steps[currentStep]?.key === 'imageGeneration'

    if (isClusteringAudio && isImageGenerationStep) {
      showModalMessage({
        title: 'Audio Generation in Progress',
        content:
          'Audio generation is currently in progress. Please wait for it to complete before proceeding.',
        type: 'info'
      })
      return
    }

    // Check for mandatory voice selection
    if (steps[currentStep]?.key === 'voiceover' && !formData.selectedVoice) {
      // message.warning('Please select a voice to proceed')
      showModalMessage({
        title: 'Voice Selection Required',
        content: 'Please select a voice to proceed',
        type: 'warning'
      })
      return
    }

    // Block navigation if user has entered text but hasn't selected a mode (shots not generated yet)
    // This usually happens in the first step 'uploadDocument'

    if (
      steps[currentStep]?.key === 'uploadDocument' &&
      formData.voiceoverText &&
      !formData.draft?.id &&
      !formData.selectedCharacter?.length &&
      !isDraftMode
    ) {
      setShowModeModal(true)
      return
    }

    // Check for missing audio links in scenes
    if (isImageGenerationStep && formData.shotList?.length > 0) {
      const sceneMissingAudio = formData.shotList.find(
        scene => !scene.audio_link
      )

      if (sceneMissingAudio) {
        showModalMessage({
          title: 'Audio Missing',
          content: (
            <div>
              <p>
                <strong>{sceneMissingAudio.title || 'A scene'}</strong> does not
                have generated audio yet.
              </p>
              <p>
                Please generate audio for this scene to proceed to the next
                step.
              </p>
            </div>
          ),
          centered: true
        })
        return
      }
    }

    handleNext()
  }

  // Render step content
  const renderStepContent = () => {
    const { key, component: StepComponent } = steps[currentStep]

    const commonProps = {
      icon: steps[currentStep].icon,
      formData,
      setFormData,
      onValidationChange: isValid => updateStepValidation(key, isValid),
      onProceed: handleSafeNext,
      onGoBack: handlePrevious,
      isGeneratingLocationReference,
      onRegenerateShots: () => shotsHook.fetchShots(),
      isClusteringAudio,
      videoId
    }

    // VideoGeneration (last step)
    if (steps[currentStep]?.key === 'videoGeneration') {
      return (
        <StepComponent
          ref={videoGenerationRef}
          {...commonProps}
          videoGenerating={videoGenerating}
          generatingVideoForAllScenes={generatingVideoForAllScenes}
          handleRegenerateVideo={handleRegenerateVideo}
          handleRetryFailedVideos={handleRetryFailedVideos}
          handleGenerateFullVideo={generateAllVideos}
          updateShotDataRef={updateShotDataRef}
          videoProvider={videoProvider}
          setScenes={setScenes}
          isCalculatingDurations={isCalculatingDurations || calculationStarting}
          calculationProgress={calculationProgress}
          onStartDurationCalculation={handleStartDurationCalculation}
          // Final video props
          isGeneratingFinalVideo={isGeneratingFinalVideo}
          finalVideoError={finalVideoError}
          onGenerateFinalVideo={generateFinalVideo}
          setFinalVideoError={setFinalVideoError}
          // Image generation props for inline editing
          handleRegenerateImage={handleRegenerateImage}
          imageProvider={imageProvider}
          isImageGenerationLoading={isImageGenerationLoading}
          isFetchingCharacters={isFetchingCharacters}
        />
      )
    }

    // ShotList
    if (key === 'shotList') {
      return (
        <StepComponent
          ref={imageGenerationRef}
          {...commonProps}
          onEditLocationReference={handleEditLocationReference}
          fetchingShots={shotsHook.isFetchingShots}
          shotsGenerationProgress={shotsHook.shotsGenerationProgress}
          locationGenerationProgress={locationGenerationProgress}
          locationGenerationStatus={locationGenerationStatus}
          fetchShots={fetchShots}
          formatSceneObj={formatSceneObj}
          onRegenerateLocationReference={() =>
            generateLocationMap(formData.locationMap)
          }
          onAddLocationReference={handleAddLocationReference}
          isFetchingCharacters={isFetchingCharacters}
          characters={characters}
          setCharacters={setCharacters}
          fetchCharacters={fetchCharacters}
          onBatchUpdateLocations={handleBatchUpdateLocations}
          onNavigateToStep={navigateToStep}
          // Prop Reference props
          isGeneratingPropReference={isGeneratingPropReference}
          propGenerationProgress={propGenerationProgress}
          propGenerationStatus={propGenerationStatus}
          onRegeneratePropReference={() => generatePropMap(formData.propMap)}
          onAddPropReference={handleAddPropReference}
          onEditPropReference={handleEditPropReference}
          onBatchUpdateProps={handleBatchUpdateProps}
        />
      )
    }

    // VideoStyle (step 2)
    if (steps[currentStep]?.key === 'videoStyle') {
      return (
        <StepComponent
          {...commonProps}
          onStyleChange={handleVideoStyleChange}
        />
      )
    }

    // Final Result (step 7)
    if (steps[currentStep]?.key === 'finalVideo') {
      return <StepComponent {...commonProps} />
    }

    // Default for other steps
    return StepComponent ? (
      <StepComponent
        {...commonProps}
        imageProvider={imageProvider}
        isImageGenerationLoading={isImageGenerationLoading}
        setIsImageGenerationLoading={setIsImageGenerationLoading}
        generateAllImages={generateAllImages}
        handleRegenerateImage={handleRegenerateImage}
        retryFailedImages={retryFailedImages}
        audioClusteringProgress={audioClusteringProgress}
        audioClusteringStatus={audioClusteringStatus}
        onNavigateToStep={navigateToStep}
        voices={voices}
        setVoices={setVoices}
        onVoiceChange={handleVoiceChangeWithClone}
        handleFileUpload={handleFileUpload}
        isUploadingImage={isUploadingImage}
        isFetchingCharacters={isFetchingCharacters}
      />
    ) : null
  }

  const allScenesGenerated = isAllScenesGenerated(formData.shotList)

  console.log({ formData })

  if (isDraftMode) {
    return (
      <div className="video-generation">
        <DraftModePage
          formData={formData}
          setFormData={setFormData}
          onExitDraftMode={() => {
            setIsDraftMode(false)
            // If they cancel, maybe we want to go back or stay here?
            // For now just return to manual mode view
          }}
          videoId={videoId}
          onFinishDraft={handleFinishDraft}
          voices={voices}
          setVoices={setVoices}
          onVoiceChange={handleVoiceChange}
          characters={characters}
          setCharacters={setCharacters}
          isFetchingCharacters={isFetchingCharacters}
          fetchCharacters={fetchCharacters}
          draftShotsHook={draftShotsHook}
          fetchShots={fetchShots}
          formatSceneObj={formatSceneObj}
        />
      </div>
    )
  }

  return (
    <Spin spinning={isFetchingVideoData}>
      {error ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: '2rem'
          }}
        >
          <Icon
            type="exclamation-circle"
            style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }}
          />
          <h2 style={{ marginBottom: '8px' }}>
            {error === 'NOT_FOUND' ? 'Video Not Found' : 'Error Loading Video'}
          </h2>
          <p style={{ color: '#666', marginBottom: '24px' }}>
            {error === 'NOT_FOUND'
              ? 'The video you are looking for does not exist or has been deleted.'
              : 'There was an unexpected error getting the video data.'}
          </p>
          <Button type="primary" onClick={fetchVideoData}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="video-generation">
            <div className="video-generation__header">
              <div className="video-generation__header_top">
                <div className="video-generation__header_info">
                  <div
                    className="video-generation__header_info-icon"
                    onClick={handleGoBack}
                  >
                    <Tooltip title="Back to Video List">
                      <Icon type="arrow-left" />
                    </Tooltip>
                  </div>
                  <div
                    className="video-generation__header_info-text"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '16px'
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: '#5057d5',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <span
                          style={{
                            background: '#eff0ff',
                            padding: '2px 8px',
                            borderRadius: '10px'
                          }}
                        >
                          Step {currentStep + 1} / {steps.length}
                        </span>
                        <span style={{ color: '#8c8c8c' }}>•</span>
                        <span>{steps[currentStep]?.title}</span>
                      </div>

                      <div
                        className="header-container-text"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                      >
                        <h1
                          className="header-title-text"
                          style={{
                            marginBottom: 0,
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#111827',
                            letterSpacing: '-0.5px',
                            lineHeight: 1.2
                          }}
                        >
                          AI Video Generator
                        </h1>
                      </div>
                      <p
                        className="header-subtitle-text"
                        style={{
                          marginBottom: 0,
                          fontSize: '13px',
                          color: '#6b7280',
                          marginTop: '2px'
                        }}
                      >
                        Follow steps to generate AI video
                      </p>
                    </div>

                    {/* Status Indicators Centered Vertically */}
                    {isGeneratingFinalVideo && (
                      <Button
                        size="small"
                        loading
                        type="primary"
                        ghost
                        style={{
                          borderRadius: '12px',
                          fontSize: '12px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        onClick={() => setShowGenerationModal(true)}
                      >
                        Generating...
                      </Button>
                    )}
                  </div>
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <NewHeader />
                </div>
              </div>

              <div className="video-generation__progress-container">
                <div
                  className="video-generation__progress-bar"
                  style={{
                    width: `${((currentStep + 1) / steps.length) * 100}%`
                  }}
                />
              </div>
            </div>

            <div className="video-generation__container">
              <div className="video-generation__content">
                {renderStepContent()}
              </div>

              <div className="video-generation__navigation">
                <div className="video-generation__navigation-left">
                  {currentStep > 0 && (
                    <Button
                      size="large"
                      onClick={handlePrevious}
                      className="nav-button"
                    >
                      <Icon type="left" />
                      {getPreviousNextStepTitles().previousStepTitle}
                    </Button>
                  )}
                </div>

                <div className="video-generation__navigation-right">
                  {currentStep > 0 && (
                    <Button
                      size="large"
                      loading={isSavingVideoData}
                      onClick={handleSaveVideoData}
                      className="nav-button nav-button--save"
                    >
                      {!isSavingVideoData && <Icon type="save" />}
                      Save
                    </Button>
                  )}

                  {shouldShowNextButton() && (
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleSafeNext}
                      disabled={!stepsValidation[steps[currentStep]?.key]}
                      className="nav-button"
                    >
                      Next: {getPreviousNextStepTitles().nextStepTitle}
                      <Icon type="right" />
                    </Button>
                  )}

                  {steps[currentStep]?.key === 'videoGeneration' && (
                    <Button
                      type="primary"
                      size="large"
                      shape="round"
                      icon="play-circle"
                      disabled={!allScenesGenerated}
                      onClick={handleOpenVideoEditor}
                    >
                      Generate Full Video
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Modal
            visible={isCloning}
            footer={null}
            closable={false}
            centered
            width={300}
            bodyStyle={{ padding: '32px 24px', textAlign: 'center' }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
              }}
            >
              <Spin size="large" />
              <h3
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#1f2937'
                }}
              >
                Cloning Video...
              </h3>
              <p
                style={{ color: '#6b7280', fontSize: '14px', marginBottom: 0 }}
              >
                Please wait while we create a new copy.
              </p>
            </div>
          </Modal>
        </>
      )}
      <ModeSelectionModal
        visible={showModeModal}
        onSelectMode={handleSelectMode}
        onCancel={() => setShowModeModal(false)}
        isSavingVideoData={isSavingVideoData}
        videoId={videoId}
      />

      {/* Global Final Video Generation Status Modal */}
      {showGenerationModal && (
        <GenerationStatusModal
          visible={showGenerationModal}
          status="loading"
          small
          title="Generating Final Video"
          subTitle="Please wait while we process your request. This may take a few minutes."
          onClose={() => {
            Modal.confirm({
              title: 'Generation in progress',
              content:
                'Final video generation is still running in the background. Are you sure you want to close this status window?',
              okText: 'Yes, Close',
              cancelText: 'Stay',
              centered: true,
              onOk: () => {
                setShowGenerationModal(false)
              }
            })
          }}
        />
      )}

      {/* Global Final Video Player Modal */}
      {showFinalVideoModal && formData.finalVideo && (
        <AIVideoModal
          visible={showFinalVideoModal}
          videoUrl={formData.finalVideo}
          title="Your AI Generated Video"
          onClose={() => setShowFinalVideoModal(false)}
        />
      )}
    </Spin>
  )
}

export default VideoAi
